import React from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { css } from "@emotion/css"
import Column from "@amzn/meridian/column"
import Row from "@amzn/meridian/row"
import { THEME } from "src/theme"

/**
 * BasePattern — the shared page template for every HHU screen.
 *
 * A sticky header with title, optional subtitle and back button, a scrollable
 * content area, and a sticky action bar. Operators wear gloves and work in
 * cold rooms under bright lighting, so we use a LIGHT theme with large touch
 * targets, generous spacing, and a WFM-green brand accent.
 */

interface BasePatternProps {
  title: string
  subtitle?: string
  onBack?: () => void
  actions?: React.ReactNode
  children: React.ReactNode
}

const shellStyle = css({
  display: "flex",
  flexDirection: "column",
  height: "100dvh",
  width: "100%",
  background: THEME.appBg,
})

const headerStyle = css({
  padding: "12px 16px",
  background: THEME.surface,
  color: THEME.text,
  flexShrink: 0,
  borderBottom: `1px solid ${THEME.border}`,
  // A thin green brand rule under the header ties every screen to the brand.
  boxShadow: `inset 0 -3px 0 ${THEME.brand}`,
})

const contentStyle = css({
  flex: 1,
  overflowY: "auto",
  padding: "16px",
})

// Keep the handheld layout tight and readable. Without a cap, on a wide
// browser/monitor the content stretches edge‑to‑edge and label/value pairs in
// cards drift far apart (label far left, value far right) — hard to scan. A
// centered max‑width keeps pairs visually connected at any viewport width while
// still filling a narrow Zebra TC screen.
const contentInnerStyle = css({
  width: "100%",
  maxWidth: "560px",
  margin: "0 auto",
})

const actionBarStyle = css({
  padding: "12px 16px",
  background: THEME.surface,
  borderTop: `1px solid ${THEME.border}`,
  flexShrink: 0,
})

const actionBarInnerStyle = css({
  width: "100%",
  maxWidth: "560px",
  margin: "0 auto",
})

const headerInnerStyle = css({
  width: "100%",
  maxWidth: "560px",
  margin: "0 auto",
})

const backButtonStyle = css({
  appearance: "none",
  background: "transparent",
  border: 0,
  color: THEME.brand,
  fontSize: "14px",
  fontWeight: 600,
  padding: "8px",
  cursor: "pointer",
  "&:focus-visible": { outline: `2px solid ${THEME.focus}`, outlineOffset: 2 },
})

const BasePattern: React.FC<BasePatternProps> = ({ title, subtitle, onBack, actions, children }) => {
  const navigate = useNavigate()
  const location = useLocation()
  // Every screen except Home itself gets a one-tap route home, so operators
  // don't have to back out screen by screen.
  const showHome = location.pathname !== "/"
  return (
    <div className={shellStyle}>
      <header className={headerStyle}>
        <div className={headerInnerStyle}>
          <Row alignmentVertical="center" alignmentHorizontal="justify" spacing="small">
            <Row alignmentVertical="center" spacing="small">
              {onBack && (
                <button
                  type="button"
                  className={backButtonStyle}
                  onClick={onBack}
                  aria-label="Back"
                >
                  ← Back
                </button>
              )}
              <Column spacing="none">
                <h3 style={{ color: THEME.text, margin: 0, fontSize: "20px", fontWeight: 700 }}>
                  {title}
                </h3>
                {subtitle && (
                  <span style={{ color: THEME.textSecondary, fontSize: "13px" }}>
                    {subtitle}
                  </span>
                )}
              </Column>
            </Row>
            {showHome && (
              <button
                type="button"
                className={backButtonStyle}
                onClick={() => navigate("/")}
                aria-label="Home"
              >
                ⌂ Home
              </button>
            )}
          </Row>
        </div>
      </header>
      <main className={contentStyle}>
        <div className={contentInnerStyle}>{children}</div>
      </main>
      {actions && (
        <div className={actionBarStyle}>
          <div className={actionBarInnerStyle}>{actions}</div>
        </div>
      )}
    </div>
  )
}

export default BasePattern
